// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $Model,
} from 'common.data';

// Global Enums
import {
  webPages,
  webSections,
} from 'common.enums';
import {
  RegattaBoatRentStatus,
} from 'common.data';

// Global Components
import * as React from 'react';

// Helpers
import ErrorLog from '@vroskus/library-sentry/dist/browser';
import {
  getImageUrl,
} from '@vroskus/library-helpers/dist/browser';
import {
  regattaBoatRentStatusLabel,
} from 'common.language';
import config from '../../../../config';

// Services
import Services from '../../../../services';

// Components
import Loader from '../../../layout/Loader';
import Section from '../../../layout/Section';

// Types
type $Props = $Translate & {
  item: $Model<'Regatta'>;
};

type $State = {
  data: Array<$Model<'RegattaBoatRent'>>;
  loading: boolean;
};

const statusLabelClass: Record<
RegattaBoatRentStatus,
string
> = Object.freeze({
  [RegattaBoatRentStatus.available]: 'success',
  [RegattaBoatRentStatus.booked]: 'secondary',
  [RegattaBoatRentStatus.reserved]: 'warning',
});

class BoatRentsSectionView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
    };
  }

  componentDidMount(): void {
    this.getData();
    this.setupSocket();
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      item,
    } = this.props;

    try {
      await this.setStateAsync({
        loading: true,
      });

      const data = await Services.Api.getRegattaBoatRentsSectionData({
        RegattaId: item.id,
      });

      await this.setStateAsync({
        data,
        loading: false,
      });
    } catch (error) {
      await this.setStateAsync({
        loading: false,
      });

      ErrorLog.exception(error as Error);
    }
  }

  setupSocket(): void {
    const {
      item,
    } = this.props;

    Services.Socket.on({
      action: (data) => {
        if (data.RegattaId === item.id && data.modelName === 'RegattaBoatRent') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  renderItemPrice(dataItem: $Model<'RegattaBoatRent'>) {
    const {
      t,
    } = this.props;

    return (dataItem.Price > 0 ? (
      <div className={'text-right whitespace-nowrap ml-2'}>
        <strong title={t('Price')}>
          {dataItem.Price}
        </strong>
        <span className={'text-gray-600'}>
          {` ${t('Eur')}`}
        </span>
      </div>
    ) : null);
  }

  renderItemTitle(dataItem: $Model<'RegattaBoatRent'>) {
    const {
      t,
    } = this.props;

    return (
      <div className={'leading-none grow uppercase'}>
        {dataItem.RegistrationNo !== null ? (
          <>
            <div className={'flex flex-row justify-between items-center'}>
              <h4
                className={'h4'}
                title={t('Reg. no.')}
              >
                {dataItem.RegistrationNo}
              </h4>
              {this.renderItemPrice(dataItem)}
            </div>
            <small title={t('Boat name')}>
              {dataItem.Name}
            </small>
          </>
        ) : (
          <div className={'flex flex-row justify-between items-center'}>
            <small title={t('Boat name')}>
              {dataItem.Name}
            </small>
            {this.renderItemPrice(dataItem)}
          </div>
        )}
      </div>
    );
  }

  renderItem(dataItem: $Model<'RegattaBoatRent'>) {
    const {
      t,
    } = this.props;

    const imageUrl: string = getImageUrl({
      cdnUrl: config.CDN_URL,
      image: dataItem.Image,
      size: 'mid',
      undefinedImage: 'boat.jpg',
    });

    return (
      <div
        className={'rounded-lg bg-white overflow-hidden relative flex items-center p-5'}
        key={dataItem.id}
      >
        <img
          alt={t('Boat for rent')}
          className={'-mx-5 -my-5 max-h-32'}
          src={imageUrl}
        />
        <div className={'flex flex-col pl-10 grow'}>
          <div className={'flex flex-row mb-1'}>
            <div className={`badge badge_outlined badge_${statusLabelClass[dataItem.Status]} uppercase py-1 my-auto mr-3`}>
              {t(regattaBoatRentStatusLabel[dataItem.Status])}
            </div>
            {this.renderItemTitle(dataItem)}
          </div>
          <div className={'mb-1'}>
            <span className={'text-gray-600'}>
              <span title={t('Email')}>
                {dataItem.Email}
              </span>
              {dataItem.Phone && (
              <>
                <span>
                  {' | '}
                </span>
                <span title={t('Phone')}>
                  {dataItem.Phone}
                </span>
              </>
              )}
            </span>
          </div>
          <div className={'overflow-y-auto h-7 leading-none'}>
            <small>
              {dataItem.Content}
            </small>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const {
      t,
    } = this.props;
    const {
      data,
      loading,
    } = this.state;

    return (data.length > 0 ? (
      <Section
        id={webSections[webPages.regattaPage].rent}
        title={t('Boats for rent')}
        transparent
      >
        <Loader
          className={'grid xl:grid-cols-3 lg:grid-cols-2 md:grid-cols-1 gap-3 -m-5 mt-3'}
          fade
          loading={loading === true}
        >
          <>
            {data.map((dataItem: $Model<'RegattaBoatRent'>) => this.renderItem(dataItem))}
          </>
        </Loader>
      </Section>
    ) : null);
  }
}

export default BoatRentsSectionView;
